import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { endOfMonth } from 'date-fns/fp/endOfMonth';
import sortBy from 'lodash/sortBy';
import isString from 'lodash/fp/isString';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { GET_MISSION_PRICINGS } from '@bloomays-lib/adapter.api-bloomer';
import type {
  GetMissionPricingsParams,
  GetMissionPricingsResponse,
  MissionPricingHistoryItem,
  PrimeSplitRoles,
} from '@bloomays-lib/types.shared';
import {
  ensureDateNotUndefined,
  formatMonthYearFr,
  getFirstValidPrice,
  getPrivateAdminDashboardMissionPath,
} from '@bloomays-lib/utils.shared';
import { HrefLink } from '@bloomays-lib/ui.shared';
import { useActiveTams } from '../../hooks/useActiveTams';
import { TamPrimeLine, type MissionPrimeLine } from '../../types/primes';
import { formatPrimeEuro, formatPrimeRate, isMissionPrimeLine } from '../../utils/monthlyPrime.utils';
import BasicTable from './BasicTable';

export type TamPrimeDetailPanelProps = {
  line: TamPrimeLine;
  referenceDate: Date;
  onClose: () => void;
};

type PrimeSplitRoleKey = keyof PrimeSplitRoles;

type PrimeSplitRoleLabelKey = 'splitRolePasseur' | 'splitRoleButeur' | 'splitRoleCapitaine' | 'splitRoleDefenseur';

const PRIME_SPLIT_ROLE_ENTRIES: Array<{ role: PrimeSplitRoleKey; labelKey: PrimeSplitRoleLabelKey }> = [
  { role: 'passeur', labelKey: 'splitRolePasseur' },
  { role: 'buteur', labelKey: 'splitRoleButeur' },
  { role: 'capitaine', labelKey: 'splitRoleCapitaine' },
  { role: 'defenseur', labelKey: 'splitRoleDefenseur' },
];

type MissionPrimeWorkedDaysField =
  | 'normalWorkedDays'
  | 'saturdaysWorkedDays'
  | 'sundayAndholidaysWorkedDays'
  | 'astreinteWorkedDays';

type MissionPrimeWorkedDaysLabelKey =
  | 'detailPanelCraWeekdays'
  | 'detailPanelCraSaturdays'
  | 'detailPanelCraSundaysAndHolidays'
  | 'detailPanelCraOnCall';

const MISSION_PRIME_WORKED_DAYS_ENTRIES: Array<{
  field: MissionPrimeWorkedDaysField;
  labelKey: MissionPrimeWorkedDaysLabelKey;
}> = [
  { field: 'normalWorkedDays', labelKey: 'detailPanelCraWeekdays' },
  { field: 'saturdaysWorkedDays', labelKey: 'detailPanelCraSaturdays' },
  { field: 'sundayAndholidaysWorkedDays', labelKey: 'detailPanelCraSundaysAndHolidays' },
  { field: 'astreinteWorkedDays', labelKey: 'detailPanelCraOnCall' },
];

const formatPricingStartDate = (startDate: Date | string | undefined): string => {
  if (startDate === undefined) {
    return '';
  }
  const date = isString(startDate) ? new Date(startDate) : startDate;
  return date.toLocaleDateString('fr-FR');
};

const formatPricingAstreinteCoef = (astreinteCoef: number | undefined): string => `${(astreinteCoef ?? 2) * 100} %`;

const isSamePricingRow = (left: MissionPricingHistoryItem, right: MissionPricingHistoryItem): boolean => {
  if (left.recordId !== undefined && right.recordId !== undefined && left.recordId === right.recordId) {
    return true;
  }
  return (
    ensureDateNotUndefined(left.startDate).getTime() === ensureDateNotUndefined(right.startDate).getTime() &&
    left.salePricePerDayWithoutTaxes === right.salePricePerDayWithoutTaxes &&
    left.purchasePricePerDayWithoutTaxes === right.purchasePricePerDayWithoutTaxes
  );
};

const TamPrimeDetailPanel = ({ line, referenceDate, onClose }: TamPrimeDetailPanelProps) => {
  const { t } = useTranslation(['primes']);
  const { getTamDisplayName } = useActiveTams();

  const occupiedSplitRoles = useMemo(
    () => PRIME_SPLIT_ROLE_ENTRIES.filter(({ role }) => line.split[role].trim() !== ''),
    [line.split],
  );

  const missionPrimeLine: MissionPrimeLine | null = isMissionPrimeLine(line) ? line : null;
  const missionAirtableId = missionPrimeLine?.airtableId.trim() ?? '';

  const {
    loading: isPricingHistoryLoading,
    error: pricingHistoryError,
    data: pricingHistoryData,
  } = useQuery<GetMissionPricingsResponse, GetMissionPricingsParams>(GET_MISSION_PRICINGS, {
    skip: missionAirtableId === '',
    variables: { missionRecordId: missionAirtableId },
    context: { clientName: 'api.bloomers' },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const missionPricings = pricingHistoryData?.missionPricings;

  const sortedPricingHistory = useMemo(() => {
    const pricings = missionPricings ?? [];
    return sortBy(pricings, [(pricing) => ensureDateNotUndefined(pricing.startDate).getTime()]).reverse();
  }, [missionPricings]);

  const referencePricingForMonth = useMemo(() => {
    if (missionPricings === undefined || missionPricings.length === 0) {
      return undefined;
    }
    return getFirstValidPrice(missionPricings, endOfMonth(referenceDate));
  }, [missionPricings, referenceDate]);

  const referenceMonthLabel = formatMonthYearFr(referenceDate);

  return (
    <Panel>
      <Head>
        <div>
          <Client>{line.clientSocietyName || '—'}</Client>
          <BloomerLabel>{line.bloomerFullName || '—'}</BloomerLabel>
        </div>
        <IconButton aria-label={t('detailPanelClose')} size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Head>

      <Section>
        <SectionTitle>{t('detailPanelShare')}</SectionTitle>
        <Line>
          <Key>{t('columnTamShare')}</Key>
          <Val>{formatPrimeRate(line.splitRate)}</Val>
        </Line>
      </Section>

      {occupiedSplitRoles.length > 0 ? (
        <Section>
          <SectionTitle>{t('detailPanelRepartition')}</SectionTitle>
          {occupiedSplitRoles.map(({ role, labelKey }) => (
            <Line key={role}>
              <Key>{t(labelKey)}</Key>
              <Val>{getTamDisplayName(line.split[role])}</Val>
            </Line>
          ))}
        </Section>
      ) : null}

      {missionPrimeLine ? (
        <Section>
          <SectionTitle>{t('detailPanelCraTitle')}</SectionTitle>
          {MISSION_PRIME_WORKED_DAYS_ENTRIES.map(({ field, labelKey }) => (
            <Line key={field}>
              <Key>{t(labelKey)}</Key>
              <Val>{missionPrimeLine[field]}</Val>
            </Line>
          ))}
        </Section>
      ) : null}

      <Section>
        <SectionTitle>{t('detailPanelMarginAndPrimeTitle')}</SectionTitle>
        <Line>
          <Key>{t('columnMargin')}</Key>
          <Val>{formatPrimeEuro(line.monthlyMargin)}</Val>
        </Line>
        <Line>
          <Key>{t('columnCommission')}</Key>
          <Val>{formatPrimeEuro(line.monthlyPrime)}</Val>
        </Line>
        <Line>
          <Key>{t('columnCommissionRate')}</Key>
          <Val>{formatPrimeRate(line.commissionRate)}</Val>
        </Line>
      </Section>

      {missionPrimeLine && missionAirtableId !== '' ? (
        <Section>
          <SectionTitle>{t('detailPanelPricingHistoryTitle')}</SectionTitle>
          {isPricingHistoryLoading ? (
            <StatusMessage>{t('detailPanelPricingLoading')}</StatusMessage>
          ) : missionPricings ? (
            sortedPricingHistory.length === 0 ? (
              <StatusMessage>{t('detailPanelPricingEmpty')}</StatusMessage>
            ) : (
              <PricingTableWrapper>
                {referencePricingForMonth ? (
                  <Line>
                    <Key>{t('detailPanelPricingReferenceForMonth', { month: referenceMonthLabel })}</Key>
                    <Val>
                      {`${formatPrimeEuro(referencePricingForMonth.salePricePerDayWithoutTaxes)} / ${formatPrimeEuro(
                        referencePricingForMonth.purchasePricePerDayWithoutTaxes,
                      )} (${formatPrimeEuro(
                        referencePricingForMonth.salePricePerDayWithoutTaxes -
                          referencePricingForMonth.purchasePricePerDayWithoutTaxes,
                      )})`}
                    </Val>
                  </Line>
                ) : (
                  <StatusMessage>{t('detailPanelPricingReferenceMissing')}</StatusMessage>
                )}
                <BasicTable
                  columns={[
                    t('detailPanelPricingStartDate'),
                    t('detailPanelPricingSale'),
                    t('detailPanelPricingPurchase'),
                    t('detailPanelPricingMargin'),
                    t('detailPanelPricingAstreinteCoef'),
                  ]}
                >
                  {sortedPricingHistory.map((pricing) => {
                    const margin = pricing.salePricePerDayWithoutTaxes - pricing.purchasePricePerDayWithoutTaxes;
                    const isReferenceForMonth =
                      referencePricingForMonth !== undefined && isSamePricingRow(pricing, referencePricingForMonth);
                    return (
                      <PricingHistoryRow
                        key={pricing.recordId ?? `${pricing.startDate}-${pricing.salePricePerDayWithoutTaxes}`}
                        $highlighted={isReferenceForMonth}
                      >
                        <td>{formatPricingStartDate(pricing.startDate)}</td>
                        <td>{formatPrimeEuro(pricing.salePricePerDayWithoutTaxes)}</td>
                        <td>{formatPrimeEuro(pricing.purchasePricePerDayWithoutTaxes)}</td>
                        <td>{formatPrimeEuro(margin)}</td>
                        <td>{formatPricingAstreinteCoef(pricing.astreinteCoef)}</td>
                      </PricingHistoryRow>
                    );
                  })}
                </BasicTable>
              </PricingTableWrapper>
            )
          ) : pricingHistoryError ? (
            <StatusMessage>{t('detailPanelPricingLoadError')}</StatusMessage>
          ) : (
            <StatusMessage>{t('detailPanelPricingEmpty')}</StatusMessage>
          )}
        </Section>
      ) : null}

      <Links>
        <HrefLink
          href={`/jobs/${line.jobFunnelProjectId}`}
          target="_blank"
          color="tertiary"
          icon={<OpenInNewIcon fontSize="small" />}
        >
          {t('viewJob')}
        </HrefLink>
        {missionAirtableId !== '' ? (
          <HrefLink
            href={getPrivateAdminDashboardMissionPath(missionAirtableId)}
            target="_blank"
            color="tertiary"
            icon={<OpenInNewIcon fontSize="small" />}
          >
            {t('viewMission')}
          </HrefLink>
        ) : null}
      </Links>
    </Panel>
  );
};

export default TamPrimeDetailPanel;

const Panel = styled('div')(() => ({
  width: 'min(420px, 92vw)',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: '20px 24px 28px',
}));

const Head = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
}));

const Client = styled('div')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: 18,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));

const BloomerLabel = styled('div')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[60],
  marginTop: 2,
}));

const Links = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
}));

const Section = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  paddingTop: 16,
  borderTop: `1px solid ${theme.palette.neutral[15]}`,
}));

const SectionTitle = styled('div')(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
}));

const Line = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
}));

const Key = styled('span')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[60],
}));

const Val = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.neutral[90],
}));

const StatusMessage = styled('div')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[60],
}));

const PricingTableWrapper = styled('div')(() => ({
  margin: '0 -8px',
  '& table': {
    margin: 0,
  },
  '& th, & td': {
    fontSize: 12,
    padding: '8px 10px',
  },
}));

const PricingHistoryRow = styled('tr', {
  shouldForwardProp: (prop) => prop !== '$highlighted',
})<{ $highlighted: boolean }>(({ theme, $highlighted }) =>
  $highlighted
    ? {
        background: theme.palette.blue[20],
        color: theme.palette.neutral[90],
        fontWeight: 600,
      }
    : {},
);
